/**
 * Admin: AI Town Inspirations — review queue for AI-generated 'why visit today' copy.
 * Workflow: admin clicks "Generate" for a town → entry saved as 'pending' →
 * admin reviews, approves (publishes) or rejects.
 * Public /towns/{slug}/inspiration only ever returns 'approved' copy.
 */
import React, { useEffect, useState } from 'react';
import { Check, RefreshCw, Sparkles, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

const TABS = [
  { value: 'pending', label: 'Pending review' },
  { value: 'approved', label: 'Approved & live' },
  { value: 'rejected', label: 'Rejected' },
];

export default function AdminInspirations() {
  const [towns, setTowns] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  function load() {
    api.get(`/admin/inspirations?status=${tab}`).then((r) => setItems(r.data.items));
  }
  useEffect(() => {
    api.get('/towns').then((r) => setTowns(r.data.items));
  }, []);
  useEffect(load, [tab]);

  async function generate(slug) {
    setErr(''); setBusy(`gen-${slug}`);
    try {
      await api.post(`/admin/towns/${slug}/inspiration/generate`);
      setTab('pending');
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Generation failed');
    } finally { setBusy(''); }
  }

  async function approve(key) {
    setBusy(`approve-${key}`);
    try { await api.post(`/admin/inspirations/${encodeURIComponent(key)}/approve`); load(); }
    finally { setBusy(''); }
  }
  async function reject(key) {
    setBusy(`reject-${key}`);
    try { await api.post(`/admin/inspirations/${encodeURIComponent(key)}/reject`); load(); }
    finally { setBusy(''); }
  }
  async function del(key) {
    if (!window.confirm('Delete this inspiration?')) return;
    setBusy(`del-${key}`);
    try { await api.delete(`/admin/inspirations/${encodeURIComponent(key)}`); load(); }
    finally { setBusy(''); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label text-heritage mb-2 inline-flex items-center gap-1.5"><Sparkles size={12}/> AI editorial</div>
          <h1 className="font-display text-4xl">Town Inspirations</h1>
        </div>
      </div>

      <HelpCallout
        title="Nothing AI-generated goes live without your green tick."
        description="Click 'Generate' below to draft a fresh 'why visit today' paragraph for any town. It saves as 'Pending review' — only after you approve does it appear on the public town page."
        appearsAt={[
          { label: 'Public town pages', where: '/towns/{slug} — Why-visit-today card (top right)' },
          { label: 'Pending queue', where: 'this page, "Pending review" tab' },
        ]}
      />

      {/* Town generator strip */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6 mb-8">
        <div className="label text-charcoal/60 mb-3">Generate a new draft</div>
        <div className="flex flex-wrap gap-2" data-testid="inspiration-towns">
          {towns.map((t) => (
            <button
              key={t.slug}
              onClick={() => generate(t.slug)}
              disabled={busy === `gen-${t.slug}`}
              data-testid={`inspiration-generate-${t.slug}`}
              className="bg-admin border border-charcoal/15 hover:border-forest rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
            >
              {busy === `gen-${t.slug}` ? <RefreshCw size={12} className="animate-spin"/> : <Sparkles size={12} className="text-mustard"/>}
              {t.name}
            </button>
          ))}
        </div>
        {err && <div className="mt-3 text-heritage text-sm" data-testid="inspiration-error">{err}</div>}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-charcoal/10 pb-3">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            data-testid={`inspiration-tab-${t.value}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.value ? 'bg-charcoal text-base' : 'bg-admin border border-charcoal/15 hover:border-charcoal'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-center text-muted py-12">Nothing in the "{TABS.find(x => x.value === tab)?.label}" queue.</div>
      ) : (
        <div className="space-y-4">
          {items.map((d) => (
            <article key={d.key} data-testid={`inspiration-card-${d.slug}-${d.status}`} className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <div className="font-display text-xl leading-tight">{d.slug}</div>
                  <div className="label text-charcoal/50 mt-1">{d.season || '—'} · {d.weather || 'no weather data'} · {d.generated_at?.slice(0, 10) || '—'}</div>
                </div>
                <div className="flex items-center gap-2">
                  {tab === 'pending' && (
                    <>
                      <button onClick={() => approve(d.key)} disabled={busy === `approve-${d.key}`} data-testid={`inspiration-approve-${d.slug}`}
                        className="bg-forest text-base px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-forest-deep disabled:opacity-50">
                        <Check size={12}/> Approve & publish
                      </button>
                      <button onClick={() => reject(d.key)} disabled={busy === `reject-${d.key}`} data-testid={`inspiration-reject-${d.slug}`}
                        className="bg-admin border border-heritage/30 text-heritage px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                        <X size={12}/> Reject
                      </button>
                    </>
                  )}
                  <button onClick={() => del(d.key)} disabled={busy === `del-${d.key}`}
                    className="bg-admin border border-charcoal/15 text-charcoal/60 hover:text-heritage px-3 py-2 rounded-full text-xs font-semibold disabled:opacity-50">
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
              <div className="bg-stone/40 border border-charcoal/5 rounded-xl p-4 whitespace-pre-wrap text-charcoal/85 text-sm leading-relaxed">
                {d.copy}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
